import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-cta transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-clarus-dark text-clarus-yellow hover:bg-clarus-dark/90 active:bg-clarus-dark/80",
        secondary:
          "bg-clarus-purple text-clarus-dark hover:bg-clarus-purple/80 active:bg-clarus-purple/70",
        outline:
          "border-2 border-clarus-dark bg-transparent text-clarus-dark hover:bg-clarus-dark hover:text-clarus-yellow",
        ghost:
          "bg-transparent text-clarus-dark hover:bg-clarus-purple/30",
        link: "bg-transparent text-clarus-dark underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-10 px-4",
        md: "h-12 px-6",
        lg: "h-14 px-8",
        xl: "h-[72px] pl-3 pr-10 gap-5 text-cta-lg",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button };
